import type { DataTableColumn, DataTableColumnGroup, DataTableRow } from '@ui/chart/DataTable';
import type { BarChartSeries } from '@ui/chart/BarChart';
import type { HeatmapFooter, HeatmapRow } from '@ui/chart/Heatmap';
import {
	RevenueScope, RevenueValues, RoomTypeFigures, RoomTypeMonth, RoomTypeProperty, RoomTypeReport, RoomTypeRow, SituationDays, VatBasis,
} from '@app/model/bi/Bi';
import { isDefaultRules, occupiedWords } from '@app/model/integration-settings/ReportRules';
import { MONTH_LABELS, occupiedOf, revenueOf } from '../Bi.utilities';

// The figures of the Room Types report (BI report 2). The formulas are those of the spec (Room Types Feature
// Specification v1, chapter 8) with the grill decisions of 2026-10-20: a ratio on a total comes from the sums of the
// total, never from an average of the rows.

export type Measure = 'occupancy' | 'revenueShare' | 'roomNightsShare';
export type ColumnSet = 'stly' | 'lyActual' | 'vs' | 'pu1' | 'pu3' | 'pu7' | 'pu14' | 'pu30';
export const PICKUP_DAYS: Record<string, SituationDays> = { pu1: 1, pu3: 3, pu7: 7, pu14: 14, pu30: 30 };

const ratio = (a: number | null, b: number | null): number | null => a === null || b === null || b === 0 ? null : a / b;

// M-02 in the revenue of the switches (ROO2-236): null = a night has no value of it.
export const revenue = (values: RevenueValues, scope: RevenueScope, vat: VatBasis): number | null => revenueOf(values, scope, vat);

// Today: the first situation (D-0).
export const today = (row: RoomTypeRow): RoomTypeFigures => row.situations[0];

// A row with nights today: sold nights of its room type, or (ROO2-272 D5) nights in its rooms on the Assigned basis. On the
// Booked basis a row can show for its STLY or LY actual alone and hold today's assigned nights: guests moved into a room
// type that nobody booked in the period.
// ROO2-315 part c1: or occupied rooms by the report rules (a day use).
export const hasNightsToday = (row: RoomTypeRow): boolean => today(row).roomNights > 0 || row.assignedRoomNights > 0
	|| occupiedOf(today(row)) > 0 || row.assignedOccupiedRooms > 0;

// M-03: revenue ÷ RN; no value when RN is 0. ROO2-315 part c1: the callers pass the occupied rooms of the report rules
// (occupiedOf), which are the room nights without rules.
export const adr = (rev: number | null, roomNights: number): number | null => ratio(rev, roomNights);

// M-11: (revenue now − last year's revenue) ÷ last year's revenue. No value when last year had no RN and no revenue.
export const change = (now: number | null, lastYear: RoomTypeFigures, scope: RevenueScope, vat: VatBasis): number | null => {
	const before = revenue(lastYear, scope, vat);
	if (lastYear.roomNights === 0 && (before === null || before === 0)) return null;
	return now === null || before === null || before === 0 ? null : (now - before) / before;
};

// PU n = today − situation D-n, in room nights (M-12 with the PU n of the Segmentation report, decision 5).
export const pickup = (row: RoomTypeRow, days: SituationDays): number => {
	const past = row.situations.find(s => s.daysBefore === days);
	return today(row).roomNights - (past?.roomNights ?? 0);
};

// M-09 (ROO2-281 D4): RN ÷ the room-days of the type in the month or the period. No value without room-days.
export const occupancy = (roomNights: number, roomDays: number): number | null => roomDays === 0 ? null : roomNights / roomDays;

// ROO2-315 part c1 (Q3): the rooms to sell of a type in a month or the period: its room-days less its out-of-order rooms
// (OPERA, when the report rules take them off; 0 otherwise). The occupied rooms of the assigned type divide by them.
export const roomsToSell = (f: { roomDays: number, outOfOrderRooms: number }): number => f.roomDays - f.outOfOrderRooms;

// ROO2-315 part c1 (Q3, Q4): the words of the Room Types occupancy for the captions: today's words without rules.
export const occupancyWords = (report: RoomTypeReport | undefined): { occupied: string, rooms: string } => {
	if (report?.rulesDiffer === true) return { occupied: 'occupied rooms (by each property’s report rules)', rooms: ' − out-of-order rooms of the type where the rules take them off' };
	const rules = report?.reportRules ?? null;
	if (rules === null || isDefaultRules(rules)) return { occupied: 'room nights', rooms: '' };
	return {
		occupied: `occupied rooms (${occupiedWords(rules).toLowerCase()})`,
		rooms: rules.outOfOrderReducesRooms ? ' − out-of-order rooms of the type (OPERA)' : '',
	};
};

// ROO2-272 D5: every occupancy counts the room that the hotel assigned (the room nights on the Assigned basis), on both bases,
// and (ROO2-281 D4) only the room types with room-days in the period, in the numerator and the denominator.
const typesWithRoomDays = (rows: RoomTypeRow[]): RoomTypeRow[] => rows.filter(r => r.kind === 'ROOM_TYPE' && r.roomDays > 0);

// The Occ % of a room type and of a property in the By property table (RQ-27), over the room-days of the period.
export const typeOccupancy = (row: RoomTypeRow): number | null => occupancy(row.assignedOccupiedRooms, roomsToSell(row));
export const propertyOccupancy = (property: RoomTypeProperty): number | null => {
	const typed = typesWithRoomDays(property.rows);
	return occupancy(typed.reduce((s, r) => s + r.assignedOccupiedRooms, 0), typed.reduce((s, r) => s + roomsToSell(r), 0));
};

export const monthLabel = (month: string, withYear: boolean): string =>
	`${MONTH_LABELS[Number(month.slice(5, 7)) - 1]}${withYear ? ` ’${month.slice(2, 4)}` : ''}`;

// The rows of a report in the order of the spec (RQ-09, RQ-24): revenue of the switches, largest first; a row with no
// revenue value after the rows with one; "No room type" last.
export const sortedRows = (rows: RoomTypeRow[], scope: RevenueScope, vat: VatBasis): RoomTypeRow[] =>
	[...rows].sort((a, b) => {
		if ((a.key === '__other') !== (b.key === '__other')) return a.key === '__other' ? 1 : -1;
		if (a.kind !== b.kind) return a.kind === 'NO_ROOM_TYPE' ? 1 : -1;
		const ra = revenue(today(a), scope, vat), rb = revenue(today(b), scope, vat);
		if (ra === null || rb === null) return ra === null ? (rb === null ? 0 : 1) : -1;
		return rb !== ra ? rb - ra : a.title.localeCompare(b.title);
	});

// --- the All table (RQ-23, RQ-24) ---------------------------------------------------------------------------------------

// lfl (ROO2-271 D1): the scope has a property with no stays in the last-year period; a block "Today like-for-like" (the
// figures that "Revenue vs" compares) then follows This year, when a last-year block shows.
export const allColumns = (sets: ColumnSet[], lfl = false): { columns: DataTableColumn[], groups: DataTableColumnGroup[] } => {
	const columns: DataTableColumn[] = [{ id: 'title', label: 'Room type' },
		{ id: 'rn', label: 'RN', format: 'number' }, { id: 'rev', label: 'Revenue', format: 'money' }];
	const groups: DataTableColumnGroup[] = [{ label: '', span: 1 }, { label: 'This year', span: 2 }];
	if (lfl && (sets.includes('stly') || sets.includes('lyActual') || sets.includes('vs'))) {
		columns.push({ id: 'lflRn', label: 'RN', format: 'number' }, { id: 'lflRev', label: 'Revenue', format: 'money' });
		groups.push({ label: 'Today like-for-like', span: 2 });
	}
	if (sets.includes('stly')) {
		columns.push({ id: 'stlyRn', label: 'RN', format: 'number' }, { id: 'stlyRev', label: 'Revenue', format: 'money' });
		groups.push({ label: 'STLY', span: 2 });
	}
	if (sets.includes('lyActual')) {
		columns.push({ id: 'lyRn', label: 'RN', format: 'number' }, { id: 'lyRev', label: 'Revenue', format: 'money' });
		groups.push({ label: 'LY actual', span: 2 });
	}
	if (sets.includes('vs')) {
		columns.push({ id: 'vsStly', label: 'vs STLY', format: 'percent', decimals: 1, signed: true, tone: 'change', sortable: false },
			{ id: 'vsLy', label: 'vs LY actual', format: 'percent', decimals: 1, signed: true, tone: 'change', sortable: false });
		groups.push({ label: lfl ? 'Revenue vs, like-for-like' : 'Revenue vs', span: 2 });
	}
	const pickups = (['pu1', 'pu3', 'pu7', 'pu14', 'pu30'] as ColumnSet[]).filter(s => sets.includes(s));
	columns.push({ id: 'adr', label: 'ADR', format: 'money', decimals: 2, sortable: false },
		...pickups.map(s => ({ id: s, label: `PU ${PICKUP_DAYS[s]}`, format: 'number' as const, signed: true, tone: 'change' as const, sortable: false })),
		{ id: 'res', label: 'Res.', format: 'number' }, { id: 'share', label: '%', format: 'percent', decimals: 1, sortable: false });
	groups.push({ label: '', span: 3 + pickups.length });
	return { columns, groups };
};

// The cells of a row (or of the total, with share 1). A last-year cell has no value when that row had no room nights
// and no revenue last year; so does its vs % cell.
export const allCells = (row: RoomTypeRow, total: RoomTypeRow, scope: RevenueScope, vat: VatBasis): DataTableRow['cells'] => {
	const now = today(row);
	const rev = revenue(now, scope, vat);
	const lastYear = (f: RoomTypeFigures) => f.roomNights === 0 && (revenue(f, scope, vat) ?? 0) === 0;
	const cells: DataTableRow['cells'] = {
		title: row.title,
		rn: now.roomNights,
		rev: rev === null ? null : Math.round(rev),
		lflRn: row.comparable.roomNights,
		lflRev: round(revenue(row.comparable, scope, vat)),
		stlyRn: lastYear(row.stly) ? null : row.stly.roomNights,
		stlyRev: lastYear(row.stly) ? null : round(revenue(row.stly, scope, vat)),
		lyRn: lastYear(row.lyActual) ? null : row.lyActual.roomNights,
		lyRev: lastYear(row.lyActual) ? null : round(revenue(row.lyActual, scope, vat)),
		vsStly: change(revenue(row.comparable, scope, vat), row.stly, scope, vat),
		vsLy: change(revenue(row.comparable, scope, vat), row.lyActual, scope, vat),
		adr: adr(rev, occupiedOf(now)),
		res: row.reservations,
		share: row === total ? 1 : ratio(rev, revenue(today(total), scope, vat)),
	};
	Object.entries(PICKUP_DAYS).forEach(([set, days]) => { cells[set] = pickup(row, days); });
	return cells;
};

const round = (v: number | null): number | null => v === null ? null : Math.round(v);

// RQ-26: Transposed = one column per room type, one row per measure, with the same figures.
export const TRANSPOSED_MEASURES: { id: string, label: string, format: DataTableColumn['format'], decimals?: number, signed?: boolean, tone?: 'change' }[] = [
	{ id: 'res', label: 'Reservations', format: 'number' },
	{ id: 'rn', label: 'Room nights', format: 'number' },
	{ id: 'rev', label: 'Revenue', format: 'money' },
	{ id: 'lflRn', label: 'Today LFL RN', format: 'number' },
	{ id: 'lflRev', label: 'Today LFL Rev', format: 'money' },
	{ id: 'stlyRn', label: 'STLY RN', format: 'number' },
	{ id: 'stlyRev', label: 'STLY Rev', format: 'money' },
	{ id: 'lyRn', label: 'LY actual RN', format: 'number' },
	{ id: 'lyRev', label: 'LY actual Rev', format: 'money' },
	{ id: 'vsStly', label: 'vs STLY %', format: 'percent', decimals: 1, signed: true, tone: 'change' },
	{ id: 'vsLy', label: 'vs LY actual %', format: 'percent', decimals: 1, signed: true, tone: 'change' },
	{ id: 'adr', label: 'ADR', format: 'money', decimals: 2 },
	{ id: 'share', label: 'Share', format: 'percent', decimals: 1 },
];
// The measures of the transposed table: the like-for-like rows only when the scope has a property with no last-year stays.
export const transposedMeasures = (lfl: boolean) => TRANSPOSED_MEASURES.filter(m => lfl || (m.id !== 'lflRn' && m.id !== 'lflRev'));

// --- the revenue bar (RQ-21) and the mix (RQ-13) -------------------------------------------------------------------------

// One stacked bar: one part per room type, as wide as its share of revenue; the types under 1 % are left out; after the
// 6th type, the rest is one gray part "All other" (the 7 series slots of the chart tokens, docs/CHARTS.md rule 6).
export const revenueBar = (rows: RoomTypeRow[], total: RoomTypeRow, scope: RevenueScope, vat: VatBasis): BarChartSeries[] => {
	const all = revenue(today(total), scope, vat);
	const shares = rows.map(r => ({ r, share: ratio(revenue(today(r), scope, vat), all) }))
		.filter((x): x is { r: RoomTypeRow, share: number } => x.share !== null && x.share >= 0.01);
	const top = shares.slice(0, 6).map(x => ({ id: x.r.key, label: x.r.title, values: [x.share] }));
	const rest = shares.slice(6).reduce((s, x) => s + x.share, 0);
	return rest > 0 ? [...top, { id: '__other', label: 'All other', role: 'other' as const, values: [rest] }] : top;
};

// M-05, M-06, M-13: the three shares of each room type with sold nights.
export const mix = (rows: RoomTypeRow[], total: RoomTypeRow, scope: RevenueScope, vat: VatBasis): { categories: string[], series: BarChartSeries[] } => {
	const sold = rows.filter(r => today(r).roomNights > 0);
	// ROO2-281 decision 6: the inventory share is of the room-days of all types in the period, also of a type with no sold
	// night in the period; a type with no room-days has none.
	const roomDays = rows.reduce((s, r) => s + r.roomDays, 0);
	return {
		categories: sold.map(r => r.title),
		series: [
			{ id: 'inventory', label: 'Inventory share', values: sold.map(r => r.roomDays === 0 ? null : ratio(r.roomDays, roomDays)) },
			{ id: 'roomNights', label: 'Room-nights share', values: sold.map(r => ratio(today(r).roomNights, today(total).roomNights)) },
			{ id: 'revenue', label: 'Revenue share', values: sold.map(r => ratio(revenue(today(r), scope, vat), revenue(today(total), scope, vat))) },
		],
	};
};

// --- the grid (RQ-09 … RQ-12) -----------------------------------------------------------------------------------------

const monthOf = (row: RoomTypeRow, month: string): RoomTypeMonth =>
	row.months.find(m => m.month === month) ?? { month, roomNights: 0, occupiedRooms: 0, roomRevenue: 0, roomRevenueNet: 0, totalRevenue: 0, totalRevenueNet: 0,
		assignedRoomNights: 0, assignedOccupiedRooms: 0, roomDays: 0, outOfOrderRooms: 0 };

export const heatmapRows = (report: RoomTypeReport, months: string[], measure: Measure, scope: RevenueScope, vat: VatBasis): HeatmapRow[] => {
	// ROO2-272 D5: occupancy also shows a row with assigned nights only, as the Assigned basis does; the shares need sold nights.
	return sortedRows(report.rows, scope, vat).filter(r => (measure === 'occupancy' ? hasNightsToday(r) : today(r).roomNights > 0)).map(r => {
		const cell = (m: string): number | null => {
			const f = monthOf(r, m), t = monthOf(report.total, m);
			if (measure === 'occupancy') return occupancy(f.assignedOccupiedRooms, roomsToSell(f));
			// M-10: no value when the type has no data that month.
			if (f.roomNights === 0) return null;
			return measure === 'revenueShare' ? ratio(revenue(f, scope, vat), revenue(t, scope, vat)) : ratio(f.roomNights, t.roomNights);
		};
		const year = measure === 'occupancy' ? occupancy(r.assignedOccupiedRooms, roomsToSell(r))
			: measure === 'revenueShare' ? ratio(revenue(today(r), scope, vat), revenue(today(report.total), scope, vat))
				: ratio(today(r).roomNights, today(report.total).roomNights);
		return {
			id: r.key, label: r.title,
			sublabel: measure === 'occupancy' ? (r.rooms === null ? 'No inventory' : `${r.rooms} ${r.rooms === 1 ? 'room' : 'rooms'}`) : undefined,
			values: months.map(cell), total: year,
		};
	});
};

// The total row (RQ-09): all types' RN ÷ all types' room-days for occupancy, over the types with room-days in the period
// (ROO2-272 D5: the RN on the Assigned basis; ROO2-281 D4); the revenue or the RN of all types for the shares. ROO2-272 D8:
// the page passes the report before "All other", so a type with room-days inside "All other" still counts.
export const heatmapFooter = (report: RoomTypeReport, months: string[], measure: Measure, scope: RevenueScope, vat: VatBasis): HeatmapFooter => {
	if (measure === 'occupancy') {
		const typed = report.rows.filter(r => r.roomDays > 0);
		const sum = (f: (r: RoomTypeRow) => number) => typed.reduce((s, r) => s + f(r), 0);
		return {
			label: 'Total occ', format: 'percent',
			values: months.map(m => occupancy(sum(r => monthOf(r, m).assignedOccupiedRooms), sum(r => roomsToSell(monthOf(r, m))))),
			total: occupancy(sum(r => r.assignedOccupiedRooms), sum(r => roomsToSell(r))),
		};
	}
	if (measure === 'revenueShare') {
		const values = months.map(m => revenue(monthOf(report.total, m), scope, vat));
		const year = revenue(today(report.total), scope, vat);
		// A month of €100,000 or more has 9 characters: the row shows thousands, so the grid keeps its width (the Year
		// column stays in view at 1280 px). The table view and the KPIs keep the exact revenue.
		const thousands = values.some(v => v !== null && v >= 100_000);
		const scale = (v: number | null) => v === null ? null : Math.round(thousands ? v / 1000 : v);
		return thousands
			? { label: 'Total revenue · € thousands', format: 'number', values: values.map(scale), total: scale(year) }
			: { label: 'Total revenue', format: 'money', values: values.map(scale), total: scale(year) };
	}
	return { label: 'Total RN', format: 'number', values: months.map(m => monthOf(report.total, m).roomNights), total: today(report.total).roomNights };
};

// RQ-10: occupancy on a fixed 0–100 % scale; a share against the highest value of the grid.
export const heatmapMax = (rows: HeatmapRow[], measure: Measure): number =>
	measure === 'occupancy' ? 1 : Math.max(0.01, ...rows.flatMap(r => [...(r.values ?? []), r.total ?? null]).filter((v): v is number => v !== null));

// --- All properties: the 12 largest room types and "All other" (decision 22) ---------------------------------------------

const sumFigures = <T extends RoomTypeFigures>(list: T[], base: T): T => list.reduce<T>((s, f) => ({
	...s,
	roomNights: s.roomNights + f.roomNights,
	occupiedRooms: occupiedOf(s) + occupiedOf(f),
	roomRevenue: s.roomRevenue === null || f.roomRevenue === null ? null : s.roomRevenue + f.roomRevenue,
	roomRevenueNet: s.roomRevenueNet === null || f.roomRevenueNet === null ? null : s.roomRevenueNet + f.roomRevenueNet,
	totalRevenue: s.totalRevenue + f.totalRevenue,
	totalRevenueNet: s.totalRevenueNet === null || f.totalRevenueNet === null ? null : s.totalRevenueNet + f.totalRevenueNet,
}), base);
const ZERO_FIGURES: RoomTypeFigures = { roomNights: 0, occupiedRooms: 0, roomRevenue: 0, roomRevenueNet: 0, totalRevenue: 0, totalRevenueNet: 0 };

// The grid and the mix of All properties: the `keep` largest room types by revenue, then one row "All other (n types)"
// with the sums of the rest. Its rooms (the label): the sum, or no value when one of its types has no room count (the merge
// rule); its room-days (ROO2-281): the sum. The table keeps every row.
export const ALL_OTHER_KEY = '__other';
export const collapseRows = (report: RoomTypeReport, scope: RevenueScope, vat: VatBasis, keep = 12): RoomTypeReport => {
	const sold = sortedRows(report.rows, scope, vat).filter(hasNightsToday);
	if (sold.length <= keep + 1) return report;
	const top = sold.slice(0, keep), rest = sold.slice(keep);
	const months = [...new Set(rest.flatMap(r => r.months.map(m => m.month)))].sort();
	const other: RoomTypeRow = {
		key: ALL_OTHER_KEY, kind: 'ROOM_TYPE', title: `All other (${rest.length} types)`,
		rooms: rest.some(r => r.rooms === null) ? null : rest.reduce((s, r) => s + (r.rooms ?? 0), 0),
		roomDays: rest.reduce((s, r) => s + r.roomDays, 0),
		properties: 0, reservations: rest.reduce((s, r) => s + r.reservations, 0),
		situations: report.total.situations.map((t, i) => sumFigures(rest.map(r => r.situations[i]), { ...ZERO_FIGURES, daysBefore: t.daysBefore })),
		stly: sumFigures(rest.map(r => r.stly), { ...ZERO_FIGURES }),
		lyActual: sumFigures(rest.map(r => r.lyActual), { ...ZERO_FIGURES }),
		comparable: sumFigures(rest.map(r => r.comparable), { ...ZERO_FIGURES }),
		assignedRoomNights: rest.reduce((s, r) => s + r.assignedRoomNights, 0),
		assignedOccupiedRooms: rest.reduce((s, r) => s + r.assignedOccupiedRooms, 0),
		outOfOrderRooms: rest.reduce((s, r) => s + r.outOfOrderRooms, 0),
		months: months.map(m => {
			const parts = rest.map(r => monthOf(r, m));
			const zero = { ...ZERO_FIGURES, month: m, assignedRoomNights: 0, assignedOccupiedRooms: 0, roomDays: 0, outOfOrderRooms: 0 };
			return { ...sumFigures(parts, zero),
				assignedRoomNights: parts.reduce((s, x) => s + x.assignedRoomNights, 0), roomDays: parts.reduce((s, x) => s + x.roomDays, 0),
				assignedOccupiedRooms: parts.reduce((s, x) => s + x.assignedOccupiedRooms, 0), outOfOrderRooms: parts.reduce((s, x) => s + x.outOfOrderRooms, 0) };
		}),
		channels: [], boards: [],
	};
	return { ...report, rows: [...top, other] };
};
